import type { MenuItem } from '@kolab/dms-base/components/layout'
import { P } from '../rbac/permissions'

/**
 * Sidebar menu configuration.
 *
 * Every leaf has a `permissions` list — the sidebar hides items whose
 * permissions the current user doesn't hold (dms-base's Shell reads the
 * permission store to filter). Parent items are hidden automatically when
 * all their visible children are hidden.
 *
 * The `permissions` field maps to the SAME strings backend nirixis-admin's
 * `authz.Catalog` publishes. Keep this in lockstep with
 * `src/rbac/permissions.ts` (the typed catalog constants below are the
 * source of truth for the strings themselves).
 *
 * Note on parent items with no `permissions`: a menu group with no explicit
 * permission (like "Tenants" or "Reports") is visible whenever ANY child is
 * visible. That matches the natural UX: you don't need a "view Tenants
 * menu" permission on top of "view Distributors list".
 */
export const menuConfig: MenuItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'DashboardOutlined',
    // Dashboard visibility follows any of its cards — a user who has none of
    // these permissions has nothing to see on the page.
    permissions: [
      P.dashboard.device_stats_card.view,
      P.dashboard.pool_availability_card.view,
      P.dashboard.upcoming_expirations_card.view,
      P.dashboard.device_activity_card.view,
    ],
  },
  {
    key: 'tenants',
    label: 'Tenants',
    path: '/distributors',
    icon: 'ClusterOutlined',
    children: [
      {
        key: 'distributors',
        label: 'Distributors',
        path: '/distributors',
        icon: 'AppstoreOutlined',
        permissions: [P.tenants.distributor_list.view],
      },
      {
        key: 'resellers',
        label: 'Resellers',
        path: '/resellers',
        icon: 'TeamOutlined',
        permissions: [P.tenants.reseller_list.view],
      },
    ],
  },
  {
    key: 'catalog',
    label: 'License Catalog',
    path: '/license-products',
    icon: 'TagsOutlined',
    permissions: [P.license_catalog.product_list.view],
  },
  {
    key: 'licenses',
    label: 'Licenses',
    path: '/licenses/distributor',
    icon: 'KeyOutlined',
    children: [
      {
        key: 'distributor-licenses',
        label: 'Distributor Pools',
        path: '/licenses/distributor',
        icon: 'DatabaseOutlined',
        permissions: [P.licenses.distributor_pool_list.view],
      },
      {
        key: 'reseller-licenses',
        label: 'Reseller Allocations',
        path: '/licenses/reseller',
        icon: 'BranchesOutlined',
        permissions: [P.licenses.reseller_allocation_list.view],
      },
    ],
  },
  {
    key: 'device-licenses',
    label: 'Device Licenses',
    path: '/devices',
    icon: 'MobileOutlined',
    permissions: [P.device_licenses.list.view],
  },
  {
    key: 'device-ops',
    label: 'Device Operations',
    path: '/device-ops',
    icon: 'DesktopOutlined',
    children: [
      {
        key: 'device-ops-list',
        label: 'Device List',
        path: '/device-ops',
        icon: 'UnorderedListOutlined',
        permissions: [P.device_operations.device_list.view],
      },
      {
        key: 'pings',
        label: 'Ping History',
        path: '/pings',
        icon: 'WifiOutlined',
        permissions: [P.device_operations.ping_history.view],
      },
      {
        key: 'commands',
        label: 'Command History',
        path: '/commands',
        icon: 'ThunderboltOutlined',
        permissions: [P.device_operations.command_history.view],
      },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    path: '/reports/pool-availability',
    icon: 'BarChartOutlined',
    children: [
      {
        key: 'reports-pools',
        label: 'Pool Availability',
        path: '/reports/pool-availability',
        icon: 'FundOutlined',
        permissions: [P.reports.pool_availability.view],
      },
      {
        key: 'reports-capacity',
        label: 'Reseller Capacity',
        path: '/reports/reseller-capacity',
        icon: 'DotChartOutlined',
        permissions: [P.reports.reseller_capacity.view],
      },
      {
        key: 'reports-expiring',
        label: 'Expiring Devices',
        path: '/reports/expiring-devices',
        icon: 'FieldTimeOutlined',
        permissions: [P.reports.expiring_devices.view],
      },
      {
        key: 'reports-device-installation',
        label: 'Device Installation',
        path: '/reports/device-installation',
        icon: 'CheckCircleOutlined',
        permissions: [P.reports.device_installation.view],
      },
      {
        key: 'reports-device-lock-unlock',
        label: 'Device Lock & Unlock',
        path: '/reports/device-lock-unlock',
        icon: 'LockOutlined',
        permissions: [P.reports.device_lock_unlock.view],
      },
    ],
  },
  {
    key: 'iam',
    label: 'Identity & Access',
    path: '/iam/users',
    icon: 'SafetyCertificateOutlined',
    children: [
      {
        key: 'iam-users',
        label: 'Users',
        path: '/iam/users',
        icon: 'UserOutlined',
        permissions: [P.identity_and_access.user_list.view],
      },
      {
        key: 'iam-roles',
        label: 'Roles',
        path: '/iam/roles',
        icon: 'SafetyOutlined',
        permissions: [P.identity_and_access.role_list.view],
      },
      {
        key: 'iam-tenants',
        label: 'Tenants',
        path: '/iam/tenants',
        icon: 'BankOutlined',
        permissions: [P.identity_and_access.tenant_list.view],
      },
    ],
  },
]
